import React, { useCallback, useState } from 'react';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import { useConfig } from '../../../defaultConfig';
import useAccountChange from '../../../fetchHooks/useAccountChange';
import { useRefreshEfaktura } from '../../../fetchHooks/useEfaktura';
import type { Account, EFaktura, ApprovalStatus } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import useTranslation from '../../../hooks/useTranslation';
import { useUnactivatedPush } from '../../../hooks/useUnactivatedPush';
import { cancelPayment } from '../../../services/paymentService';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EikaModal } from '../../EikaModal';
import EFakturaForm from './EFakturaForm';
import paymentReducer from './form/reducer';
import { getInitialState } from './form/state';
import useFormContext, { EFakturaFormContext } from './form/useFormContext';
import useValidate from './form/validate';

import '../TransactionModal.scss';

interface Props {
    show: boolean;
    setShow: React.Dispatch<boolean>;
    account?: Account;
    eFaktura: EFaktura;
}

const EFakturaModal: React.FC<Props> = ({ show, setShow, account, eFaktura }) => {
    const translated = useTranslation();
    const refreshAccounts = useAccountChange();
    const refreshEfaktura = useRefreshEfaktura();
    const [deleted, setDeleted] = useState(false);
    const {
        avtalegiroLink,
        featureToggles: { disableAvtaleGiroChanges, nudgePushOnEfaktura },
    } = useConfig();

    const { targetFromAccount } = usePaymentAccountContext();

    const { signingStatus, signingError, signingOperationPending, dynamicLinkId, dispatch } = useSigningContext();
    const { state } = useFormContext();
    const updateIfChanged = useCallback(
        (paymentStatus?: ApprovalStatus) => {
            const hasChanged = !!signingError || !!paymentStatus || deleted || signingOperationPending;
            if (hasChanged) {
                refreshEfaktura();
                if (targetFromAccount) {
                    refreshAccounts([targetFromAccount.accountId]);
                }
            }
        },
        [signingError, deleted, signingOperationPending, refreshEfaktura, targetFromAccount, refreshAccounts],
    );

    const showAvtalegiroSuggestion =
        avtalegiroLink && state.toAccount?.avtaleGiroSupported && state.kid && !disableAvtaleGiroChanges;

    const showPushSuggestion = useUnactivatedPush(nudgePushOnEfaktura);

    const onClosePayment = useCallback(() => {
        if ((signingStatus === 'PROCESSING' || signingStatus === 'CREATED') && dynamicLinkId) {
            cancelPayment(dynamicLinkId);
        }
        const paymentStatusBeforeReset = signingStatus;
        dispatch({ type: Actions.RESET });
        updateIfChanged(paymentStatusBeforeReset);
        setShow(false);
    }, [signingStatus, dynamicLinkId, updateIfChanged, dispatch, setShow]);

    return (
        <EikaModal
            id="eFaktura"
            className="transaction-modal"
            open={show}
            setOpen={setShow}
            size="wide"
            ariaLabel={translated.eFaktura.eFaktura}
            onClose={onClosePayment}
            timeOut={
                deleted || (!(showAvtalegiroSuggestion || showPushSuggestion) && signingStatus === 'COMPLETED')
                    ? 2500
                    : undefined
            }
            contentShouldStretch
            desktopMinHeight="small"
        >
            {eFaktura && (
                <EFakturaForm
                    onClose={onClosePayment}
                    onDelete={() => setDeleted(true)}
                    eFaktura={eFaktura}
                    account={account}
                />
            )}
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.EFAKTURA}>
        <EFakturaFormContext
            validateHook={useValidate}
            reducer={paymentReducer}
            initialState={getInitialState(props.eFaktura)}
        >
            <EFakturaModal {...props} />
        </EFakturaFormContext>
    </SigningContextProvider>
);
